/**
 * AppShell - Top-level layout for the new UI
 *
 * Composes: TopAppBar + BottomNavBar + HamburgerMenu + OfflineBanner + PWAInstallPrompt
 * Wraps a scrollable content area for child routes.
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Wand2, Layers, BarChart3, Menu, Library } from 'lucide-react'
import { useNavigation, type ViewId } from '@/hooks/useNavigation'
import { OfflineBanner } from '@/components/OfflineBanner'
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt'
import { HamburgerMenu } from './HamburgerMenu'
import { BottomNavBar, type NavItem } from '@/components/ui/navigation/BottomNav'
import { TopBarContentProvider, useTopBarContentValue } from '@/contexts/TopBarContent'
import { OnboardingView } from './onboarding/OnboardingView'
import { I18nProvider, useT } from '@/contexts/I18nContext'
import { PaymentProvider } from '@/contexts/PaymentContext'
import { usePayment } from '@/hooks/usePayment'
import { EnergyBadge } from '@/components/ui/EnergyBadge'
import { attributeReferral, getReferralInfo, acknowledgeRewards } from '@/lib/referral-client'
import { APP_NAME } from '@shared/constants'
import { isValidLanguagePair } from '@shared/languages'

interface AppShellProps {
  children: ReactNode
}

function isOnboarded(): boolean {
  try {
    const raw = localStorage.getItem('settings')
    if (!raw) return false
    const s = JSON.parse(raw)
    return s.onboarded === true
      || (s.onboarded === undefined && 'languagePair' in s)
  } catch {
    return false
  }
}

/** Immersive routes drop the shell chrome (top header + bottom nav) to reclaim
 *  scarce phone vertical space on focused, single-task screens — the media
 *  STUDY page (/app/media/:jobId), whose own back arrow returns to the library.
 *  Excludes /app/media (library) and /app/media/new (upload). */
function isImmersiveRoute(pathname: string): boolean {
  const m = pathname.match(/^\/app\/media\/(.+)$/)
  return !!m && m[1] !== 'new'
}

/**
 * Inner layout that reads the injected top bar content.
 * Must be rendered inside TopBarContentProvider.
 */
function AppShellInner({ children }: AppShellProps) {
  const { currentView, navigateTo } = useNavigation()
  const { pathname } = useLocation()
  const immersive = isImmersiveRoute(pathname)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [checkoutNotice, setCheckoutNotice] = useState<'success' | 'cancel' | null>(() => {
    if (typeof window === 'undefined') return null
    const flag = new URLSearchParams(window.location.search).get('checkout')
    return flag === 'success' || flag === 'cancel' ? flag : null
  })
  const [rewardNotice, setRewardNotice] = useState(false)
  const topBarContent = useTopBarContentValue()
  const t = useT()
  const { refresh: refreshPayment } = usePayment()

  const NAV_ITEMS: NavItem[] = useMemo(() => [
    { id: 'generate', icon: Wand2, label: t('nav.create.label') },
    { id: 'review', icon: Layers, label: t('nav.review') },
    { id: 'library', icon: Library, label: t('nav.library') },
    { id: 'stats', icon: BarChart3, label: t('nav.stats') },
  ], [t])

  // Post-checkout reconciliation: Stripe webhook is async, so /status may not
  // yet reflect the top-up when the user lands back on ?checkout=success. Clear
  // the param, refresh now; if balance hasn't increased, retry once after 2s.
  // The /generate 402 gate backstops if it never lands. ?checkout=cancel just
  // needs the param cleared (no payment happened, no refresh needed).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const flag = params.get('checkout')
    if (flag !== 'success' && flag !== 'cancel') return
    params.delete('checkout')
    const qs = params.toString()
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname)
    // notice is derived from the URL at mount (lazy useState) — just dismiss
    // it after a beat. setState inside setTimeout is asynchronous, not a
    // synchronous effect-body update.
    const dismiss = setTimeout(() => setCheckoutNotice(null), 5000)
    if (flag === 'success') {
      // Stripe webhook is async — /status may not yet show the top-up right
      // after payment. Refresh now, then retry once after 2s to catch the
      // webhook landing. The /generate 402 gate backstops if it never lands.
      void refreshPayment()
      setTimeout(() => void refreshPayment(), 2000)
    }
    return () => clearTimeout(dismiss)
  }, [refreshPayment])

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Referral attribution: tell the server this signup came from the stored ref
  // code, then clear it. Idempotent server-side; safe on every app load.
  useEffect(() => {
    void attributeReferral()
  }, [])

  // Reward feedback: if the inviter has unacknowledged completed referrals,
  // surface a one-time banner so they learn the referral worked (closes the
  // viral loop — silent credit kills re-invites).
  useEffect(() => {
    void getReferralInfo().then((info) => {
      if (info && info.unread_rewards > 0) setRewardNotice(true)
    })
  }, [])

  const handleDismissReward = () => {
    setRewardNotice(false)
    void acknowledgeRewards()
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMenuOpen && !(e.target as HTMLElement).closest('.hamburger-menu') && !(e.target as HTMLElement).closest('.menu-trigger')) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleMenuNavigate = (view: ViewId) => {
    navigateTo(view)
    setIsMenuOpen(false)
  }

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden bg-surface touch-pan-y">
      {/* touch-pan-y on root kills accidental pinch-zoom / double-tap-zoom across
          the app interior — touch-action intersects down the subtree, so this one
          rule covers <main>, cards, and future views. Landing/login/onboarding
          render outside AppShell and keep zoom for WCAG 2.1 SC 1.4.4 (text scaling). */}
      {/* Top App Bar — hidden on immersive routes (e.g. media study) where the
          view renders its own back nav and vertical space matters most. py-2 is
          the app-wide slim (was py-3). */}
      {!immersive && (
        <header className="sticky top-0 z-40 bg-surface-container-lowest/95 backdrop-blur-sm shadow-ambient">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-3">
              <button
                className="menu-trigger p-2 rounded-xl hover:bg-surface-container-low transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Open menu"
                aria-expanded={isMenuOpen}
              >
                <Menu className="w-6 h-6 text-on-surface" />
              </button>
              <h1 className="font-headline font-extrabold text-lg text-primary">{APP_NAME}</h1>
            </div>
            {/* Energy balance — app-wide (spec: docs/plans/energy-visibility-ux.md §1).
                Extension users land on /app/import & /app/quick and never saw the
                GenerateView-only badge, so it lives in the shared top bar. Hidden
                on immersive routes with the rest of the header (media study). */}
            <EnergyBadge />
          </div>
          {topBarContent && (
            <div className="px-4 pb-2">
              {topBarContent}
            </div>
          )}
        </header>
      )}

      {/* Inline Banners below top bar */}
      <PWAInstallPrompt />
      {!isOnline && <OfflineBanner />}
      {checkoutNotice && (
        <div
          role="status"
          className={`mx-4 mt-2 px-4 py-3 rounded-xl text-sm font-medium ${
            checkoutNotice === 'success'
              ? 'bg-primary-container text-on-primary-container'
              : 'bg-surface-container-high text-on-surface-variant'
          }`}
        >
          {checkoutNotice === 'success' ? t('payment.checkoutSuccess') : t('payment.checkoutCancelled')}
        </div>
      )}
      {rewardNotice && (
        <div
          role="status"
          className="mx-4 mt-2 px-4 py-3 rounded-xl text-sm font-medium bg-primary-container text-on-primary-container flex items-center justify-between gap-3"
        >
          <span>{t('settings.referral.rewardBanner')}</span>
          <button
            onClick={handleDismissReward}
            className="shrink-0 text-xs font-bold underline-offset-2 hover:underline"
          >
            {t('settings.referral.rewardDismiss')}
          </button>
        </div>
      )}

      {/* Hamburger Menu */}
      <HamburgerMenu
        isOpen={isMenuOpen}
        currentView={currentView}
        onNavigate={handleMenuNavigate}
        onClose={() => setIsMenuOpen(false)}
      />

      {/* Main Content */}
      <main className={`flex-1 min-h-0 overflow-y-auto mx-auto w-full max-w-lg ${immersive ? 'pb-4' : 'pb-20'}`}>
        {children}
      </main>

      {/* Bottom Navigation - hidden when the hamburger menu is open OR on an
          immersive route (it occupies the thumb zone for nothing during a focused
          study session — see isImmersiveRoute). */}
      {!isMenuOpen && !immersive && (
        <BottomNavBar
          items={NAV_ITEMS}
          active={currentView}
          onNavigate={(id) => navigateTo(id as ViewId)}
        />
      )}
    </div>
  )
}

export function AppShell({ children }: AppShellProps) {
  // P1 inline onboarding (extension-activation-quickpath.md): the quick-path
  // deep link carries the language pair — the standalone gate would re-ask a
  // question the link already answers. /app/quick with a valid lp goes straight
  // through; QuickSaveView completes onboarding on pair confirm/generate.
  const { pathname, search } = useLocation()
  const quickDeepLink =
    pathname === '/app/quick' && isValidLanguagePair(new URLSearchParams(search).get('lp') ?? '')
  const [showOnboarding, setShowOnboarding] = useState(() => !isOnboarded() && !quickDeepLink)

  if (showOnboarding) {
    return (
      <TopBarContentProvider>
        <OnboardingView onComplete={() => setShowOnboarding(false)} />
      </TopBarContentProvider>
    )
  }

  return (
    <I18nProvider>
      <PaymentProvider>
        <TopBarContentProvider key="app-ready">
          <AppShellInner>{children}</AppShellInner>
        </TopBarContentProvider>
      </PaymentProvider>
    </I18nProvider>
  )
}
